import * as React from 'react';
import useStyles from './use_styles';
import useDirection from '../direction/use_direction';
import { ProgressIndicatorInterface } from './types';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { useTheme } from '@veneer/theme';
import { formatIntlLabel } from './utils';
import useSafeTimeout from '../utils/use_safe_timeout';
import progressIndicatorI18n from './i18n';
import { deprecatedColors } from '../utils/deprecated_primitives';
import { ColorType } from '../utils/color_prop';

let colorWarned = false;

const ProgressIndicator = React.forwardRef<
  HTMLDivElement,
  ProgressIndicatorInterface &
    Omit<React.HTMLAttributes<HTMLDivElement>, keyof ProgressIndicatorInterface>
>(
  (
    {
      appearance = 'circular',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
      behavior = 'indeterminate',
      color,
      delay = 0,
      i18n,
      label,
      labelAlignment = 'leading',
      showLabel = false,
      showPercentage = false,
      size = 'small',
      trackThickness,
      value: valueProp = 0,
      ...other
    },
    ref
  ) => {
    if (
      process.env.NODE_ENV === 'development' &&
      !colorWarned &&
      deprecatedColors.includes(color as ColorType)
    ) {
      console.warn(
        `@veneer/core: The color: ${color} has been deprecated since v3.121.0 and it'll be removed by October 30, 2025`
      );
      colorWarned = true;
    }
    const [ready, setReady] = React.useState(delay === 0);
    const [setReadyTimeout] = useSafeTimeout();

    const { locale } = useDirection();

    const sizes = {
      circular: {
        small: 24,
        medium: 96,
        large: 192,
      },
      linear: {
        small: 4,
        medium: 8,
        large: 16,
      },
    };
    const finalSize = sizes[appearance][size];

    React.useEffect(() => {
      if (delay > 0) {
        setReadyTimeout(() => {
          setReady(true);
        }, delay);
      }
    }, [delay, setReadyTimeout]);

    const value = valueProp < 0 ? 0 : valueProp > 100 ? 100 : valueProp;

    const theme = useTheme();
    const styles = useStyles({
      size: finalSize,
      color,
      value,
      trackThickness,
    });

    const isHc = theme.mode === 'contrast';
    const isSharp = theme.shape === 'sharp';
    const isCircular = appearance === 'circular';
    const isLinear = appearance === 'linear';
    const isDeterminate = behavior === 'determinate';
    const isIndeterminate = behavior === 'indeterminate';

    // Circular progress indicator
    const radius = 11;
    const strokeWidth = Number(trackThickness) || 2;
    const viewBoxSize = 2 * (radius + strokeWidth);

    const renderLabel = React.useMemo(() => {
      // if both invalid
      if (isCircular && isLinear) return undefined;
      if (isCircular && size === 'small') return undefined;

      const circularLabelStyle = isCircular && styles.label.circular;
      const floatingLabelStyle = isCircular ? undefined : styles.label.floating;

      if ((showPercentage || showLabel) && isDeterminate) {
        return (
          <span
            css={[
              styles.label.base,
              circularLabelStyle,
              floatingLabelStyle,
            ].filter(Boolean)}
          >
            {formatIntlLabel(value, locale)}
          </span>
        );
      }

      if (label) {
        return (
          <span aria-hidden css={[styles.label.base, circularLabelStyle]}>
            {label}
          </span>
        );
      }

      return undefined;
    }, [
      isCircular,
      isDeterminate,
      isLinear,
      label,
      locale,
      showLabel,
      showPercentage,
      size,
      styles,
      value,
    ]);

    if (!ready) return null;

    return (
      <div
        {...other}
        css={[
          styles.root.base,
          isLinear && [
            styles.root.linear.base,
            label && styles.root.linear.withLabel.base,
            labelAlignment === 'center' && styles.root.linear.withLabel.center,
            labelAlignment === 'trailing' &&
              styles.root.linear.withLabel.trailing,
          ],
          isCircular && styles.root.circular,
          isHc && styles.root.hc,
          highContrastMediaQuery(theme),
        ]}
        ref={ref}
      >
        <div
          aria-label={
            (ariaLabel ?? isIndeterminate)
              ? (i18n?.loading ?? progressIndicatorI18n.loading)
              : undefined
          }
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
          aria-valuenow={isDeterminate ? value : undefined}
          css={[
            isCircular && styles.progressBar.circular,
            isLinear && [
              styles.progressBar.linear,
              isSharp && styles.linear.sharp,
            ],
          ]}
          role="progressbar"
        >
          {isCircular ? (
            <svg
              css={[
                styles.circular.base,
                isIndeterminate && styles.circular.indeterminate,
              ]}
              viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`} // Adjusted viewBox
            >
              <circle
                cx={viewBoxSize / 2}
                cy={viewBoxSize / 2}
                fill="none"
                r={radius}
                strokeLinecap="round"
                strokeWidth={strokeWidth}
                css={[
                  styles.circle.base,
                  isDeterminate && styles.circle.determinate,
                ]}
              />
            </svg>
          ) : (
            <>
              <div
                css={[
                  styles.linear.bar,
                  isHc && styles.linear.barHc,
                  isSharp && styles.linear.sharp,
                ]}
              />
              <div
                css={[
                  styles.linear.base,
                  isIndeterminate && styles.linear.indeterminate,
                  isDeterminate && styles.linear.determinate,
                  isSharp && styles.linear.sharp,
                ]}
              />
            </>
          )}
        </div>
        {renderLabel}
      </div>
    );
  }
);

ProgressIndicator.displayName = 'ProgressIndicator';

export default ProgressIndicator;
